import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../form/CustomTypography";
import {
  ProjectEmailerInfo,
  ProjectEmailerType,
} from "../../../../models/ProjectManagement/ProjectProductsSourcingVariants";
import { formatDate } from "../functions/formatDate";

interface SendEmailerConfirmationPopupProps {
  open: boolean;
  title: string;
  message: string;
  type: ProjectEmailerType;
  data: ProjectEmailerInfo | null;
  onClose: () => void;
  onSendPreviewEmail: (type: ProjectEmailerType) => void;
  onSendEmail: (type: ProjectEmailerType) => void;
  can_send_email: boolean;
  disabled?: boolean;
}

const SendEmailerConfirmationPopup: React.FC<
  SendEmailerConfirmationPopupProps
> = ({
  open,
  title,
  message,
  type,
  data,
  onClose,
  onSendPreviewEmail,
  onSendEmail,
  can_send_email,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
              lg: "500px",
              xl: "500px",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
              lg: "500px",
              xl: "500px",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3" style={{ fontSize: "0.9rem" }}>
            {title}
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {/* Message Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
              <CustomTypography
                variant="body2"
                style={{
                  lineHeight: "1.5",
                  color: theme.palette.customColors.blackBorder,
                  fontSize: "0.9rem",
                }}
              >
                {message}
              </CustomTypography>

              <Box>
                {data?.preview_email_sent !== undefined &&
                  data?.preview_email_sent !== null &&
                  Boolean(data!.preview_email_sent) && (
                    <CustomTypography variant="body2">
                      Last preview email sent by{" "}
                      <span style={{ fontWeight: 600 }}>
                        {data?.preview_email_sent_by_name}
                      </span>
                      {" on "}
                      <span style={{ fontWeight: 600 }}>
                        {formatDate(data?.preview_email_sent_date)}
                      </span>
                    </CustomTypography>
                  )}
              </Box>

              <Box>
                {data?.email_sent !== undefined &&
                  data?.email_sent !== null &&
                  Boolean(data!.email_sent) && (
                    <CustomTypography variant="body2">
                      {data?.emails_count} emails sent. Last email sent by{" "}
                      <span style={{ fontWeight: 600 }}>
                        {data?.email_sent_by_name}
                      </span>
                      {" on "}
                      <span style={{ fontWeight: 600 }}>
                        {formatDate(data?.email_sent_date)}
                      </span>
                    </CustomTypography>
                  )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              fontSize: "0.9rem",
              "&:hover": {
                borderColor: theme.palette.customColors.greyTextColor,
                backgroundColor: theme.palette.customColors.lightGreyColor,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => onSendPreviewEmail(type)}
            variant="contained"
            disabled={disabled || !can_send_email}
            sx={{
              backgroundColor: disabled
                ? theme.palette.customColors.greyDarkborder
                : theme.palette.customColors.blueGradientStart,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              fontSize: "0.9rem",
              "&:hover": {
                backgroundColor: disabled
                  ? theme.palette.customColors.greyDarkborder
                  : theme.palette.customColors.blueGradientHoverStart,
                transform: disabled ? "none" : "translateY(-1px)",
                boxShadow: disabled
                  ? "none"
                  : `0 4px 12px ${theme.palette.customColors.blueGradientStart}30`,
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
              },
            }}
          >
            Send Preview Email
          </Button>

          <Button
            onClick={() => onSendEmail(type)}
            variant="contained"
            disabled={disabled || !can_send_email}
            sx={{
              backgroundColor: disabled
                ? theme.palette.customColors.greyDarkborder
                : theme.palette.customColors.EmeraldGreen,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              fontSize: "0.9rem",
              "&:hover": {
                backgroundColor:
                  disabled || !can_send_email
                    ? theme.palette.customColors.greyDarkborder
                    : theme.palette.customColors.greenDark,
                transform:
                  disabled || !can_send_email ? "none" : "translateY(-1px)",
                boxShadow:
                  disabled || !can_send_email
                    ? "none"
                    : `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`,
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
              },
            }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SendEmailerConfirmationPopup;
